/**
 * Error code catalog — docs/ARCHITECTURE.md §8.2, plus the four location-specific
 * codes that §8.3's upstream allow-list maps onto.
 *
 * Every non-2xx response the API can produce carries one of these codes and
 * nothing else. The client switches on `code`; `message` is for humans and may
 * be reworded without breaking anyone.
 */
export const ERROR_CODES = {
    // ── Authentication ───────────────────────────────────────────────────────
    AUTH_REQUIRED: {
        httpStatus: 401,
        retryable: false,
        message: 'Authentication is required.',
    },
    AUTH_TOKEN_EXPIRED: {
        httpStatus: 401,
        retryable: false,
        message: 'Your session has expired. Refresh and try again.',
    },
    AUTH_INVALID_CREDENTIALS: {
        httpStatus: 401,
        retryable: false,
        message: 'Incorrect email address or password.',
    },
    AUTH_ACCOUNT_LOCKED: {
        httpStatus: 423,
        retryable: false,
        message: 'This account is temporarily locked. Try again later.',
    },
    AUTH_ACCOUNT_DISABLED: {
        httpStatus: 403,
        retryable: false,
        message: 'This account is not active.',
    },
    AUTH_PASSWORD_EXPIRED: {
        httpStatus: 401,
        retryable: false,
        message: 'Your password must be changed before you can continue.',
    },
    AUTH_REFRESH_INVALID: {
        httpStatus: 401,
        retryable: false,
        message: 'Your session could not be refreshed. Sign in again.',
    },
    AUTH_REFRESH_REUSED: {
        httpStatus: 401,
        retryable: false,
        message: 'Your session was ended for security reasons. Sign in again.',
    },
    AUTH_TOTP_INVALID: {
        httpStatus: 401,
        retryable: false,
        message: 'That code is not correct. Try again.',
    },
    AUTH_TOTP_CHALLENGE_EXPIRED: {
        httpStatus: 401,
        retryable: false,
        message: 'That sign-in attempt has expired. Sign in again.',
    },
    // ── Authorization ────────────────────────────────────────────────────────
    FORBIDDEN_PERMISSION: {
        httpStatus: 403,
        retryable: false,
        message: 'You do not have permission to do that.',
    },
    FORBIDDEN_FEATURE: {
        httpStatus: 403,
        retryable: false,
        message: 'That capability is not available on this account.',
    },
    FORBIDDEN_TENANT: {
        httpStatus: 403,
        retryable: false,
        message: 'That account is not accessible from this session.',
    },
    /**
     * The browser origin the request came from is not on the allow-list.
     *
     * **Its own code, because reusing `FORBIDDEN_TENANT` sent the reader to the
     * wrong problem entirely** — a blocked origin rendered as "choose an account
     * first", and it cost a full diagnosis cycle during Phase 06a before anyone
     * realised the request had never reached a tenant check at all.
     *
     * The message says *that* the origin was refused and nothing more. Naming the
     * allowed origins, or hinting at how many there are, would turn a refusal
     * into a configuration disclosure for exactly the caller we just refused.
     */
    FORBIDDEN_ORIGIN: {
        httpStatus: 403,
        retryable: false,
        message: 'This request came from an origin the API does not accept.',
    },
    // ── Request shape ────────────────────────────────────────────────────────
    VALIDATION_FAILED: {
        httpStatus: 400,
        retryable: false,
        message: 'The request could not be processed. Check the highlighted fields.',
    },
    NOT_FOUND: {
        httpStatus: 404,
        retryable: false,
        message: 'Not found.',
    },
    CONFLICT: {
        httpStatus: 409,
        retryable: false,
        message: 'That change conflicts with the current state.',
    },
    IDEMPOTENCY_MISMATCH: {
        httpStatus: 409,
        retryable: false,
        message: 'This idempotency key was already used with a different request body.',
    },
    PAYLOAD_TOO_LARGE: {
        httpStatus: 413,
        retryable: false,
        message: 'That upload is too large.',
    },
    UNSUPPORTED_MEDIA_TYPE: {
        httpStatus: 415,
        retryable: false,
        message: 'That content type or file type is not supported.',
    },
    RATE_LIMITED: {
        httpStatus: 429,
        retryable: true,
        message: 'Too many requests. Try again shortly.',
    },
    // ── Upstream (normalised; the upstream vendor is never named) ────────────
    UPSTREAM_REJECTED: {
        httpStatus: 422,
        retryable: false,
        message: 'The request was rejected by the network operator.',
    },
    UPSTREAM_NOT_ENTITLED: {
        httpStatus: 403,
        retryable: false,
        message: 'This account is not entitled to that operation.',
    },
    UPSTREAM_AUTH_FAILED: {
        httpStatus: 503,
        retryable: true,
        message: 'A service connection problem prevented that request. Try again shortly.',
    },
    UPSTREAM_RATE_LIMITED: {
        httpStatus: 503,
        retryable: true,
        message: 'The service is busy. Try again shortly.',
    },
    UPSTREAM_UNAVAILABLE: {
        httpStatus: 503,
        retryable: true,
        message: 'The service is temporarily unavailable. Try again shortly.',
    },
    UPSTREAM_MALFORMED: {
        httpStatus: 502,
        retryable: true,
        message: 'The service returned an unexpected response.',
    },
    VENDOR_NOT_CONFIGURED: {
        httpStatus: 503,
        retryable: false,
        message: 'This account is not connected to the network operator yet.',
    },
    JOB_NOT_READY: {
        httpStatus: 409,
        retryable: true,
        message: 'That is still being prepared. Try again shortly.',
    },
    // ── Location outcomes referenced by the §8.3 allow-list ──────────────────
    // Listed here so the Phase 02 mapping table has real codes to target. No
    // route raises them in Phase 01.
    LOCATION_NOT_WHITELISTED: {
        httpStatus: 409,
        retryable: false,
        message: 'This number is not enabled for location lookup.',
    },
    LOCATION_DEVICE_OFF: {
        httpStatus: 409,
        retryable: true,
        message: 'The device is switched off.',
    },
    LOCATION_DEVICE_UNREACHABLE: {
        httpStatus: 409,
        retryable: true,
        message: 'The device is unreachable.',
    },
    LOCATION_ROAMING_BLOCKED: {
        httpStatus: 409,
        retryable: false,
        message: 'The device is in a protected roaming area.',
    },
    // ── Catch-all ────────────────────────────────────────────────────────────
    INTERNAL_ERROR: {
        httpStatus: 500,
        retryable: true,
        message: 'Something went wrong on our side.',
    },
};
export const ERROR_CODE_KEYS = Object.keys(ERROR_CODES);
/** The 29 codes enumerated by ARCHITECTURE §8.2 (excludes the 4 LOCATION_* codes). */
export const ARCHITECTURE_ERROR_CODES = ERROR_CODE_KEYS.filter((c) => !c.startsWith('LOCATION_'));
export function httpStatusFor(code) {
    return ERROR_CODES[code].httpStatus;
}
export function isRetryable(code) {
    return ERROR_CODES[code].retryable;
}
export function defaultMessageFor(code) {
    return ERROR_CODES[code].message;
}
export function isErrorCode(value) {
    return Object.prototype.hasOwnProperty.call(ERROR_CODES, value);
}
//# sourceMappingURL=errors.js.map